import React, { useEffect, useId, useRef, useState } from 'react';
import { CalendarDays, ChevronDown } from 'lucide-react';
import { Portal } from './Portal.jsx';
import { tabsOutOf, useAnchoredPopover } from './usePopover.js';
import { CalendarHeader, CalendarMonth } from './Calendar.jsx';
import {
  commitTypedField,
  formatDisplayDate,
  monthOf,
  orderedRange,
  shiftMonth,
  typedRange,
} from '../../lib/dateInput.js';

// The one-month layout's breakpoint in controls.css: keep the two in sync.
function useNarrow(query = '(max-width: 840px)') {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.(query).matches,
  );
  useEffect(() => {
    const media = window.matchMedia?.(query);
    if (!media) return undefined;
    const onChange = () => setNarrow(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [query]);
  return narrow;
}

// One field for a report period: the trigger shows the preset name (or
// Custom) and the dates; the popover has the presets on the left, two months
// on the right (one on a phone) and typed From/To at the bottom. A preset
// applies at once; a picked or typed range waits for Apply, and Cancel or
// Escape leaves the period as it was.
export function DateRangePicker({
  from,
  to,
  onChange,
  presets,
  presetRange,
  activePreset,
  ariaLabel = 'Date range',
}) {
  const reactId = useId();
  const baseId = `range-${reactId}`;
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const narrow = useNarrow();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ from, to });
  const [picking, setPicking] = useState(false);
  const [hover, setHover] = useState(null);
  const [view, setView] = useState(() => monthOf(from));
  const [focusDate, setFocusDate] = useState(from);
  const [fromText, setFromText] = useState('');
  const [toText, setToText] = useState('');
  const [invalid, setInvalid] = useState({ from: false, to: false });

  const style = useAnchoredPopover({
    open,
    onClose: (reason) => cancel(reason === 'escape'),
    anchorRef: triggerRef,
    popoverRef,
  });

  // Start in the dialog on its active preset (else the first) once it is
  // placed — it renders hidden until then, and a hidden element cannot take
  // focus.
  const placed = open && style.visibility !== 'hidden';
  useEffect(() => {
    if (!placed) return;
    const presetsEl = popoverRef.current?.querySelector('.range-presets');
    (
      presetsEl?.querySelector('[aria-pressed="true"]') ||
      presetsEl?.querySelector('button')
    )?.focus({ preventScroll: true });
  }, [placed]);

  const months = narrow ? [view] : [view, shiftMonth(view, 1)];
  const activeLabel =
    presets.find((preset) => preset.key === activePreset)?.label || 'Custom';

  function syncTexts(range) {
    setFromText(formatDisplayDate(range.from));
    setToText(formatDisplayDate(range.to));
    setInvalid({ from: false, to: false });
  }

  function openPopover() {
    const range = { from, to };
    setDraft(range);
    syncTexts(range);
    setPicking(false);
    setHover(null);
    setView(monthOf(from));
    setFocusDate(from);
    setOpen(true);
  }

  function cancel(returnFocus = true) {
    setOpen(false);
    setPicking(false);
    if (returnFocus) triggerRef.current?.focus();
  }

  function apply(range = draft) {
    if (!range.from || !range.to) return;
    setOpen(false);
    setPicking(false);
    triggerRef.current?.focus();
    if (range.from !== from || range.to !== to) onChange(range);
  }

  // Apply takes what the fields show, not the draft: a blank field skips the
  // blur commit and an unreadable one only flags itself, so the draft can
  // still hold a range the fields no longer show.
  function applyTyped() {
    const typed = typedRange(fromText, toText);
    if (!typed.range) {
      setInvalid(typed.invalid);
      return;
    }
    apply(typed.range);
  }

  function choosePreset(key) {
    const range = presetRange(key);
    if (range) apply(range);
  }

  function pick(iso) {
    if (!picking) {
      setDraft({ from: iso, to: null });
      setFromText(formatDisplayDate(iso));
      setToText('');
      setPicking(true);
      setHover(iso);
      return;
    }
    const range = orderedRange(draft.from, iso);
    setDraft(range);
    syncTexts(range);
    setPicking(false);
    setHover(null);
  }

  function commitTyped(which) {
    const text = which === 'from' ? fromText : toText;
    const otherText = which === 'from' ? toText : fromText;
    const result = commitTypedField(text, otherText, picking);
    if (result.invalid) {
      setInvalid((state) => ({ ...state, [which]: true }));
      return null;
    }
    if (!result.range) {
      // The other field is blank or unreadable: tidy this one only, so
      // Apply and Enter still see (and flag) what the other one shows.
      (which === 'from' ? setFromText : setToText)(
        formatDisplayDate(result.date),
      );
      setInvalid((state) => ({ ...state, [which]: false }));
      return null;
    }
    const { range } = result;
    setDraft(range);
    syncTexts(range);
    setPicking(false);
    setHover(null);
    setView(monthOf(range.from));
    return range;
  }

  function onFocusDate(next) {
    setFocusDate(next);
    if (picking) setHover(next);
    const target = monthOf(next);
    const last = months[months.length - 1];
    const before =
      target.year * 12 + target.month < view.year * 12 + view.month;
    const after = target.year * 12 + target.month > last.year * 12 + last.month;
    if (before) setView(target);
    else if (after) setView(shiftMonth(target, -(months.length - 1)));
  }

  const shown =
    picking && hover
      ? orderedRange(draft.from, hover)
      : { from: draft.from, to: draft.to };
  const draftPreset = !picking
    ? presets.find((preset) => {
        const range = presetRange(preset.key);
        return range && range.from === draft.from && range.to === draft.to;
      })?.key
    : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`range-trigger${open ? ' is-open' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${ariaLabel}: ${activeLabel}, ${formatDisplayDate(from)} to ${formatDisplayDate(to)}`}
        onClick={() => (open ? cancel() : openPopover())}
      >
        <CalendarDays
          size={20}
          aria-hidden="true"
          className="range-trigger-icon"
        />
        <span className={`range-badge${activePreset ? '' : ' is-custom'}`}>
          {activeLabel}
        </span>
        <span className="range-dates">
          {formatDisplayDate(from)} – {formatDisplayDate(to)}
        </span>
        <ChevronDown size={20} aria-hidden="true" className="range-chevron" />
      </button>
      {open && (
        <Portal>
          <div
            ref={popoverRef}
            className="range-popover"
            role="dialog"
            aria-label={ariaLabel}
            style={style}
            onMouseDown={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
              if (tabsOutOf(event, popoverRef.current)) cancel();
            }}
          >
            <div className="range-popover-body">
              <div className="range-presets" role="group" aria-label="Presets">
                {presets.map((preset) => (
                  <button
                    key={preset.key}
                    type="button"
                    aria-pressed={draftPreset === preset.key}
                    onClick={() => choosePreset(preset.key)}
                  >
                    {preset.label}
                  </button>
                ))}
                <span
                  className={`range-presets-custom${!draftPreset ? ' is-on' : ''}`}
                >
                  Custom
                </span>
              </div>
              <div className="range-months">
                {/* Keyed by slot, not month: paging must keep the Previous /
                    Next button that was just pressed mounted and focused. */}
                {months.map((m, i) => (
                  <div className="range-month" key={i}>
                    <CalendarHeader
                      id={`${baseId}-m${i}`}
                      year={m.year}
                      month={m.month}
                      onPrev={
                        i === 0 ? () => setView((v) => shiftMonth(v, -1)) : null
                      }
                      onNext={
                        i === months.length - 1
                          ? () => setView((v) => shiftMonth(v, 1))
                          : null
                      }
                    />
                    <CalendarMonth
                      year={m.year}
                      month={m.month}
                      range={shown}
                      focusDate={focusDate}
                      onFocusDate={onFocusDate}
                      onPick={pick}
                      onHover={(iso) => picking && setHover(iso)}
                      labelledBy={`${baseId}-m${i}`}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="range-popover-foot">
              <div className="range-typed">
                <label htmlFor={`${baseId}-from`}>From</label>
                <input
                  id={`${baseId}-from`}
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="DD.MM.YYYY"
                  value={fromText}
                  aria-invalid={invalid.from || undefined}
                  className={invalid.from ? 'is-invalid' : ''}
                  onChange={(event) => {
                    setFromText(event.target.value);
                    setInvalid((state) => ({ ...state, from: false }));
                  }}
                  onBlur={() => fromText.trim() && commitTyped('from')}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter') return;
                    event.preventDefault();
                    const range = commitTyped('from');
                    if (range) apply(range);
                    else applyTyped();
                  }}
                />
                <label htmlFor={`${baseId}-to`}>To</label>
                <input
                  id={`${baseId}-to`}
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="DD.MM.YYYY"
                  value={toText}
                  aria-invalid={invalid.to || undefined}
                  className={invalid.to ? 'is-invalid' : ''}
                  onChange={(event) => {
                    setToText(event.target.value);
                    setInvalid((state) => ({ ...state, to: false }));
                  }}
                  onBlur={() => toText.trim() && commitTyped('to')}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter') return;
                    event.preventDefault();
                    const range = commitTyped('to');
                    if (range) apply(range);
                    else applyTyped();
                  }}
                />
              </div>
              <div className="range-actions">
                <button
                  type="button"
                  className="text-button"
                  onClick={() => cancel()}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="primary"
                  disabled={picking || !draft.from || !draft.to}
                  onClick={applyTyped}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}
    </>
  );
}
